import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useWindowDimensions } from "react-native";

import UsersView from "./UsersView";
import ListEmpty from "@/features/invitations/components/molecules/ListEmpty";
import UserItem from "@/features/invitations/components/molecules/UserItem";

import SkeletonList from "@/components/organisms/SkeletonList";
import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import {
  InvitationsOrganizationUserFragment,
  useInvitationsListMyOrganizationsOrganizationUsersQuery,
} from "@/generated/graphql";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { ifWeb } from "@/utils/platform";

/**
 * Initial page size for the Manage Users list. The backend defaults to 20 when
 * `first` is omitted, which capped the list at 20 regardless of how many users
 * the org has (SE-5186 / SE-5371). Request a page large enough to hold every
 * org's user base in one fetch; the `fetchMore` wiring below still covers the
 * unlikely case of more than this many users.
 */
const USERS_PAGE_SIZE = 500;

/**
 * NativeWind `md:` breakpoint (768px). The content panel fixes its width at md+
 * and otherwise fills the viewport — a screen-width decision derived from the
 * measured window width rather than the vendored Gluestack breakpoint hook.
 */
const MD_BREAKPOINT = 768;

/**
 * Container component for the users management screen.
 * Fetches and displays organization users with pagination and refresh support.
 */
const UsersContainer = () => {
  const { data, fetchMore, refetch, loading, networkStatus } =
    useInvitationsListMyOrganizationsOrganizationUsersQuery({
      variables: { first: USERS_PAGE_SIZE },
    });

  const isRefreshing = useMemo(
    () => networkStatus === NetworkStatus.refetch,
    [networkStatus]
  );

  const organizationUsers = useMemo(
    () =>
      data?.listMyOrganizationsOrganizationUsers?.edges.map(
        edge => edge.node
      ) ?? [],
    [data?.listMyOrganizationsOrganizationUsers?.edges]
  );

  const endCursor = useMemo(
    () =>
      data?.listMyOrganizationsOrganizationUsers?.pageInfo.hasNextPage
        ? data?.listMyOrganizationsOrganizationUsers?.pageInfo.endCursor
        : null,
    [
      data?.listMyOrganizationsOrganizationUsers?.pageInfo.endCursor,
      data?.listMyOrganizationsOrganizationUsers?.pageInfo.hasNextPage,
    ]
  );

  const handleLoadMore = useCallback(() => {
    if (endCursor && !loading) {
      fetchMore({
        variables: { after: endCursor },
      });
    }
  }, [endCursor, fetchMore, loading]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const renderItem: ListRenderItem<InvitationsOrganizationUserFragment> =
    useCallback(({ item }) => <UserItem item={item} />, []);

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && organizationUsers.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [loading, isRefreshing, organizationUsers.length]
  );

  const renderListEmptyComponent = useCallback(
    () =>
      loading ? (
        <SkeletonList type="manage-users" />
      ) : (
        <ListEmpty title="No Users" />
      ),
    [loading]
  );

  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? ifWeb(640, 750) : width;

  useSetPageTitle("Users");

  return (
    <UsersView
      organizationUsers={organizationUsers}
      refreshing={isRefreshing}
      widthContent={widthContent}
      renderItem={renderItem}
      renderListFooterComponent={renderListFooterComponent}
      renderListEmptyComponent={renderListEmptyComponent}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
    />
  );
};

export default UsersContainer;
